import { useState } from 'react';
import { useLocation, useNavigate, Outlet, useParams } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { Plus, ChevronDown, Wrench, FileUp, ToolCase, FolderPlus } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import ImportToolModal from './ToolsList/ImportToolModal';
import ToolboxFilter from './ToolsList/ToolboxFilter';
import { RenderIf } from '@/components/RenderIf';
import type { ImportedTool } from '@/hooks/tools/useImportTool/types';
import { useListFolders, useFolderManagement } from '@/hooks';
import useFolderBrowserParams from '@/hooks/useFolderBrowserParams';
import FolderBreadcrumbs from '@/components/FolderBrowserLayout/FolderBreadcrumbs';
import CreateFolderModal from '../workflows/CreateFolderModal';
import RenameFolderModal from '../workflows/RenameFolderModal';
import {
    ToolsContainer,
    ToolsContent,
    ToolsHeader,
    TabsContentContainer,
    HeaderSpacer,
} from './styled';

function Tools() {
    const location = useLocation();
    const navigate = useNavigate();
    const { agentId } = useParams();
    const queryClient = useQueryClient();
    const [isImportModalOpen, setIsImportModalOpen] = useState(false);

    // Determine current tab based on URL
    const currentTab = location.pathname.includes('/toolboxes') ? 'toolboxes' : 'tools';

    // Folder browser params (URL-based state) - separate params for tools and toolboxes
    const { currentPath: toolCurrentPath, setCurrentPath: setToolCurrentPath } =
        useFolderBrowserParams({ folderParamKey: 'toolFolder' });
    const { currentPath: toolboxCurrentPath, setCurrentPath: setToolboxCurrentPath } =
        useFolderBrowserParams({ folderParamKey: 'toolboxFolder' });

    // Fetch folders for tools
    const { folders: toolFolders, isLoading: isLoadingToolFolders } = useListFolders({
        agentId: agentId!,
        entityType: 'tool',
    });

    // Fetch folders for toolboxes
    const { folders: toolboxFolders, isLoading: isLoadingToolboxFolders } = useListFolders({
        agentId: agentId!,
        entityType: 'toolbox',
    });

    // Folder management for tools
    const toolFolderManagement = useFolderManagement({
        agentId: agentId!,
        entityType: 'tool',
        currentPath: toolCurrentPath,
        setCurrentPath: setToolCurrentPath,
    });

    // Folder management for toolboxes
    const toolboxFolderManagement = useFolderManagement({
        agentId: agentId!,
        entityType: 'toolbox',
        currentPath: toolboxCurrentPath,
        setCurrentPath: setToolboxCurrentPath,
    });

    // Use correct management based on current tab
    const folderManagement =
        currentTab === 'tools' ? toolFolderManagement : toolboxFolderManagement;
    const {
        isCreateFolderModalOpen,
        createFolderParentPath,
        renameFolderState,
        handleCreateFolder,
        handleCreateSubfolder,
        handleCloseCreateFolder,
        handleRenameFolder,
        handleCloseRenameFolder,
        handleDeleteFolder,
    } = folderManagement;

    // Use correct folders based on current tab
    const folders = currentTab === 'tools' ? toolFolders : toolboxFolders;
    const isLoadingFolders =
        currentTab === 'tools' ? isLoadingToolFolders : isLoadingToolboxFolders;
    const activePath = currentTab === 'tools' ? toolCurrentPath : toolboxCurrentPath;
    const setActivePath = currentTab === 'tools' ? setToolCurrentPath : setToolboxCurrentPath;

    const handleTabChange = ({ tabValue }: { tabValue: string }) => {
        const basePath = location.pathname.split('/tools')[0] + '/tools';
        const newPath = tabValue === 'toolboxes' ? `${basePath}/toolboxes` : basePath;
        navigate(newPath);
    };

    const [shouldOpenToolDrawer, setShouldOpenToolDrawer] = useState(false);
    const [shouldOpenToolboxDrawer, setShouldOpenToolboxDrawer] = useState(false);
    const [importedTool, setImportedTool] = useState<ImportedTool | null>(null);

    const handleCreateSystemTool = () => {
        setShouldOpenToolDrawer(true);
    };

    const handleCreateToolbox = () => {
        setShouldOpenToolboxDrawer(true);
    };

    const handleImportClick = () => {
        setIsImportModalOpen(true);
    };

    const handleImportModalClose = () => {
        setIsImportModalOpen(false);
    };

    const handleImportSuccess = async (tool: ImportedTool) => {
        handleImportModalClose();
        await queryClient.invalidateQueries({ queryKey: ['tools', agentId] });
        setImportedTool(tool);
    };

    return (
        <ToolsContainer>
            <ToolsContent>
                <Tabs
                    value={currentTab}
                    onValueChange={value => handleTabChange({ tabValue: value })}
                    className="flex flex-col h-full min-h-0"
                >
                    <ToolsHeader>
                        <TabsList className="grid min-w-[200px] w-64 grid-cols-2">
                            <TabsTrigger value="tools" className="gap-2">
                                <Wrench size={14} />
                                Tools
                            </TabsTrigger>
                            <TabsTrigger value="toolboxes" className="gap-2">
                                <ToolCase size={14} />
                                Toolboxes
                            </TabsTrigger>
                        </TabsList>

                        <FolderBreadcrumbs currentPath={activePath} onNavigate={setActivePath} />

                        <HeaderSpacer />

                        <RenderIf isTrue={currentTab === 'tools'}>
                            <ToolboxFilter agentId={agentId!} />
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button className="gap-2">
                                        <Plus size={16} />
                                        Create
                                        <ChevronDown size={14} />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                    <DropdownMenuItem onClick={handleCreateSystemTool}>
                                        <Wrench size={16} />
                                        Create System Tool
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={handleImportClick}>
                                        <FileUp size={16} />
                                        Import Tool
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={handleCreateFolder}>
                                        <FolderPlus size={16} />
                                        New Folder
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </RenderIf>

                        <RenderIf isTrue={currentTab === 'toolboxes'}>
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button className="gap-2">
                                        <Plus size={16} />
                                        Create
                                        <ChevronDown size={14} />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                    <DropdownMenuItem onClick={handleCreateToolbox}>
                                        <ToolCase size={16} />
                                        Create Toolbox
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={handleCreateFolder}>
                                        <FolderPlus size={16} />
                                        New Folder
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </RenderIf>
                    </ToolsHeader>

                    <TabsContentContainer>
                        <Outlet
                            context={{
                                shouldOpenToolDrawer,
                                setShouldOpenToolDrawer,
                                shouldOpenToolboxDrawer,
                                setShouldOpenToolboxDrawer,
                                importedTool,
                                setImportedTool,
                                currentPath: activePath,
                                setCurrentPath: setActivePath,
                                onCreateFolder: handleCreateFolder,
                                onCreateSubfolder: handleCreateSubfolder,
                                onRenameFolder: handleRenameFolder,
                                onDeleteFolder: handleDeleteFolder,
                                folders,
                                isLoadingFolders,
                            }}
                        />
                    </TabsContentContainer>
                </Tabs>

                <ImportToolModal
                    isOpen={isImportModalOpen}
                    onRequestClose={handleImportModalClose}
                    agentId={agentId!}
                    onSuccess={handleImportSuccess}
                />

                <CreateFolderModal
                    isOpen={isCreateFolderModalOpen}
                    onClose={handleCloseCreateFolder}
                    agentId={agentId!}
                    entityType={currentTab === 'tools' ? 'tool' : 'toolbox'}
                    currentPath={createFolderParentPath}
                />

                <RenameFolderModal
                    isOpen={renameFolderState.isOpen}
                    onClose={handleCloseRenameFolder}
                    folderId={renameFolderState.folderId}
                    folderPath={renameFolderState.folderPath}
                    currentName={renameFolderState.folderName}
                    currentPath={activePath}
                    setCurrentPath={setActivePath}
                    agentId={agentId!}
                    entityType={currentTab === 'tools' ? 'tool' : 'toolbox'}
                />
            </ToolsContent>
        </ToolsContainer>
    );
}

export default Tools;
